import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { OAuthGrantSummary } from '@sss/sdk';
import { sdk } from '../../lib/sdk';

/** Apps the user authorised through the OAuth server (e.g. MCP clients), with a way to disconnect them. */
export default function ConnectedAppsTab() {
  const { t } = useTranslation();
  const [grants, setGrants] = useState<OAuthGrantSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    sdk.oauthGrants.list().then(
      (list) => {
        if (!cancelled) setGrants(list);
      },
      (err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed');
      },
    );
    return () => {
      cancelled = true;
    };
  }, []);

  async function onDisconnect(grantId: string) {
    setError(null);
    try {
      await sdk.oauthGrants.revoke(grantId);
      setGrants((prev) => prev?.filter((g) => g.grantId !== grantId) ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed');
    }
  }

  if (!grants && !error) return <p>{t('common.loading')}</p>;

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      <h3 style={{ margin: 0 }}>{t('settings.connectedApps.title')}</h3>
      <p style={{ color: '#666', margin: 0 }}>{t('settings.connectedApps.description')}</p>
      {grants?.length === 0 && <p style={{ color: '#666' }}>{t('settings.connectedApps.none')}</p>}
      <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {grants?.map((grant) => (
          <li key={grant.grantId} style={row}>
            <div>
              <strong>{grant.clientName || t('settings.connectedApps.unknownApp')}</strong>
              {!grant.verified && <span style={badge}>{t('settings.connectedApps.unverified')}</span>}
              <div style={{ color: '#666', fontSize: '0.85rem' }}>
                {grant.scopes.join(', ')} · {t('settings.connectedApps.grantedAt', { date: new Date(grant.grantedAt).toLocaleDateString() })}
              </div>
            </div>
            <button type="button" onClick={() => onDisconnect(grant.grantId)}>
              {t('settings.connectedApps.disconnect')}
            </button>
          </li>
        ))}
      </ul>
      {error && <p style={{ color: 'red' }}>{error}</p>}
    </section>
  );
}

const row: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', padding: '0.75rem', border: '1px solid #ddd', borderRadius: 4 };
const badge: React.CSSProperties = { marginLeft: '0.5rem', fontSize: '0.75rem', padding: '0.1rem 0.4rem', borderRadius: 3, background: '#fff4e5', border: '1px solid #f0b46a' };
